import AddaImg from "@/assets/images/Adda.jpg";
import { useState } from "react";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";
import { Button } from "../ui/button";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/use-translation";

const useSendEmail = () => {
   const queryClient = useQueryClient();
   const { toast } = useToast();
   return useMutation({
      mutationFn: (data: {
         name: string;
         lastName: string;
         email: string;
         phone?: string;
         notes?: string;
      }) => {
         return axios.post("/api/email/sendContactEmail", data);
      },
      onSuccess: () => {
         toast({
            title: "Email sent successfully",
            description: "We will get back to you as soon as possible.",
         });
         queryClient.invalidateQueries({ queryKey: ["contacts"] });
      },
   });
};

export default function ContactsSection() {
   const [firstName, setFirstName] = useState("");
   const [lastName, setLastName] = useState("");
   const [email, setEmail] = useState("");
   const [message, setMessage] = useState("");
   const [phone, setPhone] = useState("");
   const { mutate: sendEmail } = useSendEmail();
   const { translation } = useTranslation();
   return (
      <section
         id="contacts"
         className={`
        bg-custom-dark rounded-lg mt-4
        px-4 py-8 mx-4
        sm:px-6 sm:py-12 sm:mx-6
        lg:flex lg:flex-row lg:justify-between lg:px-20 lg:py-16 lg:mx-10
      `}
      >
         {/* ── Contact info ───────────────────────────────────── */}
         <aside className="flex flex-col">
            <h6 className="text-d-accent uppercase text-custom-light-blue">
               {translation.contact.title}
            </h6>

            <div className="mt-8 mr-4 lg:mt-28 flex flex-col gap-4 text-white/60">
               <div className="flex items-center gap-3">
                  <img
                     src={AddaImg}
                     alt="Adda Hafborg"
                     className="w-12 h-12 rounded-full object-cover"
                  />
                  <div>
                     <p className="text-d-body-m-line leading-5 text-white">
                        {translation.contact.name}
                     </p>
                     <p className="text-d-body-m-line leading-5 text-white">
                        {translation.contact.position}
                     </p>
                  </div>
               </div>

               <address className="not-italic flex flex-col gap-2">
                  <a href="mailto:adda@nordroof.com">
                     <span className="text-d-body-m-line text-custom-white-60">
                        {translation.contact.email}
                     </span>
                  </a>
                  <a href="tel:8887175">
                     <span className="text-d-body-m-line text-custom-white-60">
                        {translation.contact.phone}
                     </span>
                  </a>
               </address>
            </div>
         </aside>

         {/* ── Form ───────────────────────────────────────────── */}
         <div className="flex-1 flex flex-col gap-10 mt-10 lg:mt-0">
            <h2 className="text-d-h2 text-custom-white">
               {translation.contact.form.title}
               <br />
               {translation.contact.form.subtitle}
            </h2>

            <form className="flex flex-col gap-6">
               {/* inputs: stack on mobile, row on md+ */}
               <div className="flex flex-col md:flex-row gap-5 w-full">
                  <div className="flex flex-col gap-3 flex-1 w-full">
                     <Input
                        variant="dark"
                        placeholder={translation.contact.form.firstName}
                        value={firstName}
                        onChange={(e) => setFirstName(e.target.value)}
                     />
                     <Input
                        variant="dark"
                        placeholder={translation.contact.form.lastName}
                        value={lastName}
                        onChange={(e) => setLastName(e.target.value)}
                     />
                     <Input
                        variant="dark"
                        placeholder={translation.contact.form.email}
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                     />
                     <Input
                        variant="dark"
                        placeholder={translation.contact.form.phone}
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                     />
                  </div>

                  <Textarea
                     variant="dark"
                     placeholder={translation.contact.form.message}
                     value={message}
                     onChange={(e) => setMessage(e.target.value)}
                     className="flex-1 w-full"
                     rows={4}
                  />
               </div>

               {/* checkbox */}
               <label className="inline-flex items-center gap-3">
                  <Checkbox theme="dark" />
                  <span className="text-d-body-m-line text-custom-white-60">
                     {translation.contact.form.notice}
                  </span>
               </label>

               <Button
                  onClick={(e) => {
                     e.preventDefault();
                     sendEmail({
                        name: firstName,
                        lastName,
                        email,
                        phone,
                        notes: message,
                     });
                  }}
                  variant="l-dark"
                  className="w-28"
               >
                  {translation.contact.form.submit}
               </Button>
            </form>
         </div>
      </section>
   );
}
